
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import axios from "axios";
import {
    DataGrid,
    GridToolbar,
} from "@mui/x-data-grid";

import { Link } from "react-router-dom";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { baseUrl } from "../Univ.js";

import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";


// ============================================================
// CATEGORY CONFIGURATION
// ============================================================

const CATEGORIES = [
    "Consulting",
    "Lab",
    "CT",
    "Xray",
    "Scan",
    "Dialysis",
    "Inj",
    "Obs",
    "Echo",
    "Uroflow",
    "Minor",
    "Audiology",
    "Dental",
];


// Minor category IDs from main_mincat.
//
// 8  = Dialysis
// 12 = Inj
// 13 = Obs
// 14 = Echo
// 15 = Uroflow

const MINOR_CATEGORY_MAP = {
    8: "Dialysis",
    12: "Inj",
    13: "Obs",
    14: "Echo",
    15: "Uroflow",
};


const toNumber = (value) => {
    const number = Number(value ?? 0);

    return Number.isFinite(number) ? number : 0;
};


const roundAmount = (value) =>
    Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;


const money = (value) =>
    toNumber(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });


const normalize = (value) =>
    String(value ?? "").trim().toLowerCase();


// ============================================================
// MINOR CATEGORY IDENTIFICATION
// ============================================================

const getMinorCategory = (component) => {

    const category = component?.cat || {};

    const categoryId = Number(category.id);

    const categoryName = normalize(category.cat);

    // First identify using the actual database category ID.
    if (MINOR_CATEGORY_MAP[categoryId]) {
        return MINOR_CATEGORY_MAP[categoryId];
    }

    // Support existing Audiology and Dental categories.
    if (categoryName.includes("audiology")) {
        return "Audiology";
    }

    if (categoryName.includes("dental")) {
        return "Dental";
    }

    // Existing categories that do not belong to the
    // newly separated categories remain under Minor.
    return "Minor";
};


// ============================================================
// CATEGORY AMOUNTS
// ============================================================

const getCategoryAmounts = () => {

    const result = {};

    CATEGORIES.forEach((category) => {

        result[category] = {
            total: 0,
            paid: 0,
            balance: 0,
            discount: 0,
        };

    });

    return result;
};


// ============================================================
// DISTRIBUTE INVOICE AMOUNTS BY COMPONENT VALUE
// ============================================================

const distributeAmount = (amount, components) => {

    const result = {};

    const validComponents = components.filter(
        (item) => item.amount > 0
    );

    const totalComponentAmount = validComponents.reduce(
        (sum, item) => sum + item.amount,
        0
    );

    if (!validComponents.length || totalComponentAmount <= 0) {
        return result;
    }

    let distributed = 0;

    validComponents.forEach((item, index) => {

        let allocated;

        if (index === validComponents.length - 1) {

            // Allocate the remaining amount to avoid
            // rounding differences.
            allocated = roundAmount(amount - distributed);

        } else {

            allocated = roundAmount(
                amount * item.amount / totalComponentAmount
            );

            distributed = roundAmount(
                distributed + allocated
            );

        }

        result[item.category] =
            roundAmount(
                (result[item.category] || 0) + allocated
            );

    });

    return result;
};


// ============================================================
// INVOICE HELPERS
// ============================================================

const getInvoiceType = (item) => {

    if (item.invoice) return "consulting";

    if (item.lab_invoice) return "lab";

    if (item.inv_invoice) return "inv";

    if (item.min_invoice) return "minor";

    return "unknown";
};


const getInvoiceNumber = (item) =>
    item.invoice ||
    item.lab_invoice ||
    item.inv_invoice ||
    item.min_invoice ||
    "";


const getInvoiceLink = (item) => {

    if (item.invoice) {
        return `/op-invoice/${item.invoice}`;
    }

    if (item.lab_invoice) {
        return `/lab-invoice-op/${item.lab_invoice}`;
    }

    if (item.inv_invoice) {
        return `/inv-invoice-op/${item.inv_invoice}`;
    }

    if (item.min_invoice) {
        return `/min-invoice-op/${item.min_invoice}`;
    }

    return "";
};


// ============================================================
// MAIN COMPONENT
// ============================================================

function PaymentDetail() {

    const [startDate, setStartDate] = useState(null);

    const [endDate, setEndDate] = useState(null);

    const [cashier, setCashier] = useState("");

    const [consultant, setConsultant] = useState("all");

    const [mode, setMode] = useState("");

    const [userData, setUserData] = useState([]);

    const [combinedData, setCombinedData] = useState([]);

    const [refundData, setRefundData] = useState([]);

    const [dueData, setDueData] = useState([]);

    const [activeTab, setActiveTab] = useState("invoiced");

    const [isLoading, setIsLoading] = useState(false);

    const [error, setError] = useState("");


    // ========================================================
    // DATE FORMAT
    // ========================================================

    const formatDate = (date) => {

        if (!date) return "";

        const year = date.getFullYear();

        const month = String(date.getMonth() + 1).padStart(2, "0");

        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };


    // ========================================================
    // LOAD USERS
    // ========================================================

    useEffect(() => {

        let cancelled = false;

        axios
            .get(`${baseUrl}user-search/`)
            .then((response) => {

                if (!cancelled) {

                    setUserData(
                        Array.isArray(response.data)
                            ? response.data
                            : []
                    );

                }

            })
            .catch((err) => {
                console.error("Unable to load users:", err);
            });

        return () => {
            cancelled = true;
        };

    }, []);


    // ========================================================
    // FETCH COLLECTION DATA
    // ========================================================

    const fetchData = useCallback(async () => {

        setIsLoading(true);

        setError("");

        try {

            const response = await axios.get(
                `${baseUrl}all-collection-view/`,
                {
                    params: {
                        start_date: formatDate(startDate),
                        end_date: formatDate(endDate),
                        cashier,
                        mode,
                        status: 2,
                    },
                }
            );

            const data = Array.isArray(response.data)
                ? response.data
                : [];


            // =================================================
            // PROCESS EACH INVOICE
            // =================================================

            const formattedData = await Promise.all(

                data.map(async (item, index) => {

                    const minidArray = item.min_id
                        ? String(item.min_id)
                            .split(",")
                            .map((id) => id.trim())
                            .filter(Boolean)
                        : [];


                    const itestidArray = item.itest_id
                        ? String(item.itest_id)
                            .split(",")
                            .map((id) => id.trim())
                            .filter(Boolean)
                        : [];


                    // =========================================
                    // FETCH MINOR COMPONENTS AND IMAGING
                    // =========================================

                    const [minResponses, invResponses] =
                        await Promise.all([

                            Promise.all(
                                minidArray.map((id) =>
                                    axios.get(
                                        `${baseUrl}edit-minor-comp/${id}`
                                    )
                                )
                            ),

                            Promise.all(
                                itestidArray.map((id) =>
                                    axios.get(
                                        `${baseUrl}edit-components/${id}`
                                    )
                                )
                            ),

                        ]);


                    // =========================================
                    // MINOR COMPONENT INFORMATION
                    // =========================================

                    const minorComponents = minResponses.map(
                        (res) => {

                            const component = res.data;

                            return {
                                category: getMinorCategory(component),

                                amount: toNumber(component?.amount),

                                componentName:
                                    component?.comp || "",

                                categoryName:
                                    component?.cat?.cat || "",

                                categoryId:
                                    component?.cat?.id || null,
                            };

                        }
                    );


                    const minData = [
                        ...new Set(
                            minorComponents.map(
                                (item) => item.categoryName
                            )
                        ),
                    ];


                    // =========================================
                    // IMAGING COMPONENTS
                    // =========================================

                    const invData = invResponses
                        .map((res) => res.data?.cat)
                        .filter(Boolean);


                    // =========================================
                    // INITIALIZE ALL CATEGORIES
                    // =========================================

                    const amounts = getCategoryAmounts();


                    // =========================================
                    // CONSULTING
                    // =========================================

                    const consultingPaid = toNumber(item.paid);

                    const consultingBalance = toNumber(item.balance);

                    amounts.Consulting = {

                        total: toNumber(
                            item.fee ??
                            consultingPaid + consultingBalance
                        ),

                        paid: consultingPaid,

                        balance: consultingBalance,

                        discount: toNumber(item.disc),

                    };


                    // =========================================
                    // LAB
                    // =========================================

                    amounts.Lab = {

                        total: toNumber(item.lab_tot),

                        paid: toNumber(item.lab_paid),

                        balance: toNumber(item.lab_balance),

                        discount: toNumber(item.lab_disc),

                    };


                    // =========================================
                    // MINOR CATEGORY DISTRIBUTION
                    // =========================================

                    if (minorComponents.length > 0) {

                        // Combine component amounts belonging
                        // to the same category.

                        const categoryComponents = {};

                        minorComponents.forEach((component) => {

                            const category = component.category;

                            categoryComponents[category] =
                                (categoryComponents[category] || 0) +
                                component.amount;

                        });


                        const components = Object.entries(
                            categoryComponents
                        ).map(([category, amount]) => ({
                            category,
                            amount,
                        }));


                        // Allocate total, paid, balance and
                        // discount proportionally.

                        const minorTotal = distributeAmount(
                            toNumber(item.min_tot),
                            components
                        );

                        const minorPaid = distributeAmount(
                            toNumber(item.min_paid),
                            components
                        );

                        const minorBalance = distributeAmount(
                            toNumber(item.min_balance),
                            components
                        );

                        const minorDiscount = distributeAmount(
                            toNumber(item.min_disc),
                            components
                        );


                        CATEGORIES.forEach((category) => {

                            amounts[category] = {

                                total: toNumber(
                                    minorTotal[category]
                                ),

                                paid: toNumber(
                                    minorPaid[category]
                                ),

                                balance: toNumber(
                                    minorBalance[category]
                                ),

                                discount: toNumber(
                                    minorDiscount[category]
                                ),

                            };

                        });

                    } else if (
                        toNumber(item.min_tot) ||
                        toNumber(item.min_paid) ||
                        toNumber(item.min_balance)
                    ) {

                        // Preserve old records if their
                        // component information is unavailable.

                        amounts.Minor = {

                            total: toNumber(item.min_tot),

                            paid: toNumber(item.min_paid),

                            balance: toNumber(item.min_balance),

                            discount: toNumber(item.min_disc),

                        };

                    }


                    // =========================================
                    // IMAGING CATEGORY CLASSIFICATION
                    // =========================================

                    if (invData.includes("CT")) {

                        amounts.CT = {

                            total: toNumber(item.inv_tot),

                            paid: toNumber(item.inv_paid),

                            balance: toNumber(item.inv_balance),

                            discount: toNumber(item.inv_disc),

                        };

                    } else if (
                        invData.includes("Xray") ||
                        invData.includes("X-ray")
                    ) {

                        amounts.Xray = {

                            total: toNumber(item.inv_tot),

                            paid: toNumber(item.inv_paid),

                            balance: toNumber(item.inv_balance),

                            discount: toNumber(item.inv_disc),

                        };

                    } else if (
                        invData.includes("USG") ||
                        invData.includes("Scan")
                    ) {

                        amounts.Scan = {

                            total: toNumber(item.inv_tot),

                            paid: toNumber(item.inv_paid),

                            balance: toNumber(item.inv_balance),

                            discount: toNumber(item.inv_disc),

                        };

                    }


                    // =========================================
                    // INVOICE INFORMATION
                    // =========================================

                    const type = getInvoiceType(item);

                    const invoiceNumber = getInvoiceNumber(item);

                    const invoiceLink = getInvoiceLink(item);

                    const rowId = [
                        type,
                        item.id ?? index,
                        invoiceNumber || item.appt_id || "",
                    ].join("-");


                    // =========================================
                    // RETURN FINAL ROW
                    // =========================================

                    return {

                        ...item,

                        id: rowId,

                        date: item.add_date_date ? `${item.add_date_date} ${item.add_time || ""}`.trim() : "",

                        appointmentId: item.appt_id || "",

                        patientId: item.pat__pat_id || "",

                        patientName: item.pat__patient || "",

                        consultantName:
                            item.cons__consultant ||
                            item.consultant ||
                            "",

                        cashierName: item.cashier || "",


                        invoice: invoiceNumber,

                        invoiceLink,

                        sourceType: type,


                        // Consulting
                        fee: amounts.Consulting.paid,
                        fee1: amounts.Consulting.total,
                        feeb: amounts.Consulting.balance,
                        consultingDisc: amounts.Consulting.discount,


                        // Lab
                        lab: amounts.Lab.paid,
                        labt: amounts.Lab.total,
                        labb: amounts.Lab.balance,
                        labDisc: amounts.Lab.discount,


                        // Imaging
                        ct: amounts.CT.paid,
                        ctt: amounts.CT.total,
                        ctb: amounts.CT.balance,
                        ctDisc: amounts.CT.discount,

                        xray: amounts.Xray.paid,
                        xrayt: amounts.Xray.total,
                        xrayb: amounts.Xray.balance,
                        xrayDisc: amounts.Xray.discount,

                        scan: amounts.Scan.paid,
                        scant: amounts.Scan.total,
                        scanb: amounts.Scan.balance,
                        scanDisc: amounts.Scan.discount,


                        // Minor categories
                        dialysis: amounts.Dialysis.paid,
                        dialysist: amounts.Dialysis.total,
                        dialysisb: amounts.Dialysis.balance,
                        dialysisDisc: amounts.Dialysis.discount,

                        inj: amounts.Inj.paid,
                        injt: amounts.Inj.total,
                        injb: amounts.Inj.balance,
                        injDisc: amounts.Inj.discount,

                        obs: amounts.Obs.paid,
                        obst: amounts.Obs.total,
                        obsb: amounts.Obs.balance,
                        obsDisc: amounts.Obs.discount,

                        echo: amounts.Echo.paid,
                        echot: amounts.Echo.total,
                        echob: amounts.Echo.balance,
                        echoDisc: amounts.Echo.discount,

                        uroflow: amounts.Uroflow.paid,
                        uroflowt: amounts.Uroflow.total,
                        uroflowb: amounts.Uroflow.balance,
                        uroflowDisc: amounts.Uroflow.discount,

                        minor: amounts.Minor.paid,
                        minort: amounts.Minor.total,
                        minorb: amounts.Minor.balance,
                        minorDisc: amounts.Minor.discount,

                        audiology: amounts.Audiology.paid,
                        audiologyt: amounts.Audiology.total,
                        audiologyb: amounts.Audiology.balance,
                        audiologyDisc: amounts.Audiology.discount,

                        dental: amounts.Dental.paid,
                        dentalt: amounts.Dental.total,
                        dentalb: amounts.Dental.balance,
                        dentalDisc: amounts.Dental.discount,


                        mode:
                            item.mode ||
                            item.lab_pmode ||
                            item.inv_pmode ||
                            item.min_pmode ||
                            "",


                        categoryAmounts: amounts,

                        minData,

                        invData,

                    };

                })

            );


            setCombinedData(formattedData);

        } catch (err) {

            console.error(
                "Unable to load OP payment report:",
                err
            );

            setError(
                err.response?.data?.detail ||
                err.response?.data?.error ||
                "Unable to load the OP report. Please try again."
            );

            setCombinedData([]);

        } finally {

            setIsLoading(false);

        }

    }, [startDate, endDate, cashier, mode]);


    useEffect(() => {
        fetchData();
    }, [fetchData]);


    // ========================================================
    // REFUND AND DUE REPORTS
    // ========================================================

    useEffect(() => {

        let cancelled = false;

        const fetchOtherReports = async () => {

            try {

                const params = {

                    start_date: formatDate(startDate),

                    end_date: formatDate(endDate),

                };


                const [refundResponse, dueResponse] =
                    await Promise.all([

                        axios.get(
                            `${baseUrl}refund-report-view/`,
                            { params }
                        ),

                        axios.get(
                            `${baseUrl}due-report-view/`,
                            { params }
                        ),

                    ]);


                if (!cancelled) {

                    setRefundData(
                        Array.isArray(refundResponse.data)
                            ? refundResponse.data
                            : []
                    );

                    setDueData(
                        Array.isArray(dueResponse.data)
                            ? dueResponse.data
                            : []
                    );

                }

            } catch (err) {

                console.error(
                    "Unable to load refund/due reports:",
                    err
                );

            }

        };


        fetchOtherReports();

        return () => {
            cancelled = true;
        };

    }, [startDate, endDate]);


    // ========================================================
    // CONSULTANT FILTER
    // ========================================================

    const consultants = useMemo(() => {

        const names = combinedData
            .map((row) => row.consultantName)
            .filter(Boolean);

        return [...new Set(names)].sort(
            (a, b) => a.localeCompare(b)
        );

    }, [combinedData]);


    const filteredData = useMemo(

        () => combinedData.filter((row) => {

            return (
                consultant === "all" ||
                normalize(row.consultantName) ===
                normalize(consultant)
            );

        }),

        [combinedData, consultant]

    );


    // ========================================================
    // REPORT ROWS
    // ========================================================

    const getAmount = (row, category, type) =>
        row.categoryAmounts?.[category]?.[type] ?? 0;


    const rowsCollected = useMemo(

        () => filteredData.filter((row) =>

            CATEGORIES.some(
                (category) =>
                    getAmount(row, category, "paid") > 0
            )

        ),

        [filteredData]

    );


    const rowsPending = useMemo(

        () => filteredData.filter((row) =>

            CATEGORIES.some(
                (category) =>
                    getAmount(row, category, "balance") > 0
            )

        ),

        [filteredData]

    );


    const rowsDiscount = useMemo(

        () => filteredData.filter((row) =>

            CATEGORIES.some(
                (category) =>
                    getAmount(row, category, "discount") > 0
            )

        ),

        [filteredData]

    );


    const reportRows = useMemo(() => {

        if (activeTab === "collected") {
            return rowsCollected;
        }

        if (activeTab === "pending") {
            return rowsPending;
        }

        if (activeTab === "discount") {
            return rowsDiscount;
        }

        return filteredData;

    }, [
        activeTab,
        filteredData,
        rowsCollected,
        rowsPending,
        rowsDiscount,
    ]);


    const reportAmountType = {

        invoiced: "total",

        collected: "paid",

        pending: "balance",

        discount: "discount",

    }[activeTab];


    const reportTitle = {

        invoiced: "Invoiced Report",

        collected: "Collection Report",

        refund: "Refund Report",

        paidDues: "Paid Dues Report",

        discount: "Discount Report",

        pending: "Pending Dues Report",

    }[activeTab];


    // ========================================================
    // CATEGORY TOTALS
    // ========================================================

    const categoryTotals = useMemo(() => {

        const totals = {};

        CATEGORIES.forEach((category) => {
            totals[category] = 0;
        });


        reportRows.forEach((row) => {

            CATEGORIES.forEach((category) => {

                totals[category] += getAmount(
                    row,
                    category,
                    reportAmountType
                );

            });

        });


        totals.grandTotal = CATEGORIES.reduce(

            (sum, category) =>
                sum + totals[category],

            0

        );


        return totals;

    }, [reportRows, reportAmountType]);


    // Separate discount totals for category cards.
    const categoryDiscountTotals = useMemo(() => {

        const totals = {};

        CATEGORIES.forEach((category) => {
            totals[category] = 0;
        });


        reportRows.forEach((row) => {

            CATEGORIES.forEach((category) => {

                totals[category] += getAmount(
                    row,
                    category,
                    "discount"
                );

            });

        });


        return totals;

    }, [reportRows]);


    // ========================================================
    // PAYMENT DATAGRID COLUMNS
    // ========================================================

    const paymentColumns = useMemo(() => {

        const columns = [
            {
                field: "sno",
                headerName: "S.No",
                width: 65,
                sortable: false,
                filterable: false,
                renderCell: (params) =>
                    params.api.getRowIndexRelativeToVisibleRows(
                        params.id
                    ) + 1,
            },

            {
                field: "date",
                headerName: "Date",
                width: 130,
            },

            {
                field: "appointmentId",
                headerName: "Appointment ID",
                width: 130,
            },

            {
                field: "patientId",
                headerName: "Patient ID",
                width: 105,
            },

            {
                field: "patientName",
                headerName: "Patient Name",
                width: 160,
            },

            {
                field: "consultantName",
                headerName: "Consultant",
                width: 165,
                valueGetter: (_value, row) =>
                    row.consultantName || "—",
            },

            {
                field: "invoice",
                headerName: "Invoice No",
                width: 155,
                sortable: false,

                renderCell: (params) =>

                    params.row.invoiceLink ? (

                        <Link
                            to={params.row.invoiceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="payment-ip-invoice-link"
                        >
                            {params.row.invoice}
                        </Link>

                    ) : (

                        params.row.invoice || "—"

                    ),

            },


            // ================================================
            // CATEGORY COLUMNS
            // ================================================

            ...CATEGORIES.map((category) => ({

                field: `category_${category}`,

                headerName: category,

                width: 115,

                type: "number",

                valueGetter: (_value, row) =>
                    getAmount(
                        row,
                        category,
                        reportAmountType
                    ),

                renderCell: (params) =>
                    money(params.value),

            })),


            // ================================================
            // TOTAL DISCOUNT COLUMN
            // ================================================

            {
                field: "discountTotal",

                headerName: "Discount",

                width: 125,

                type: "number",

                sortable: true,

                valueGetter: (_value, row) =>

                    CATEGORIES.reduce(

                        (sum, category) =>

                            sum + getAmount(
                                row,
                                category,
                                "discount"
                            ),

                        0

                    ),

                renderCell: (params) =>
                    money(params.value),

            },


            {
                field: "mode",
                headerName: "Mode",
                width: 110,
            },

            {
                field: "cashierName",
                headerName: "Cashier",
                width: 130,
            },

        ];


        return columns;

    }, [reportAmountType]);


    // ========================================================
    // REFUND / DUE ROWS
    // ========================================================

    const refundRows = useMemo(

        () => refundData.map((item, index) => ({

            ...item,

            id: `refund-${item.id ?? index}`,

            dateTime: `${item.add_date_date || ""} ${
                item.add_time || ""
            }`.trim(),

        })),

        [refundData]

    );


    const dueRows = useMemo(

        () => dueData.map((item, index) => ({

            ...item,

            id: `due-${item.id ?? index}`,

            dateTime: `${item.add_date_date || ""} ${
                item.add_time || ""
            }`.trim(),

        })),

        [dueData]

    );


    // ========================================================
    // AUXILIARY DATAGRID COLUMNS
    // ========================================================

    const auxiliaryColumns = useMemo(

        () => [

            {
                field: "dateTime",
                headerName: "Date / Time",
                width: 160,
            },

            {
                field: "pat__pat_id",
                headerName: "Patient ID",
                width: 110,
            },

            {
                field: "pat__patient",
                headerName: "Patient",
                width: 165,
            },

            {
                field: "invoice",
                headerName: "Invoice",
                width: 125,
            },

            {
                field: "receipt",
                headerName: "Receipt",
                width: 145,

                renderCell: (params) => {

                    const receipt = params.row.receipt;

                    const route =
                        activeTab === "refund"
                            ? `/refund-invoice-op/${receipt}`
                            : `/due-invoice-op/${receipt}`;


                    return receipt ? (

                        <Link
                            to={route}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="payment-ip-invoice-link"
                        >
                            {receipt}
                        </Link>

                    ) : (

                        "—"

                    );

                },

            },

            {
                field: "invoice_amount",
                headerName: "Invoiced",
                width: 125,
                valueGetter: (value) => toNumber(value),
                renderCell: (params) => money(params.value),
            },

            {
                field: "paid_amount",
                headerName: "Paid",
                width: 115,
                valueGetter: (value) => toNumber(value),
                renderCell: (params) => money(params.value),
            },

            ...(activeTab === "refund"

                ? [

                    {
                        field: "refund_amount",
                        headerName: "Refund",
                        width: 115,
                        valueGetter: (value) => toNumber(value),
                        renderCell: (params) => money(params.value),
                    },

                ]

                : [

                    {
                        field: "due_amount",
                        headerName: "Due Amount",
                        width: 115,
                        valueGetter: (value) => toNumber(value),
                        renderCell: (params) => money(params.value),
                    },

                ]),


            {
                field: "balance",
                headerName: "Balance",
                width: 115,
                valueGetter: (value) => toNumber(value),
                renderCell: (params) => money(params.value),
            },

            {
                field: "receivedby",
                headerName: "Received By",
                width: 130,
            },

            {
                field: "reason",
                headerName: "Reason",
                width: 150,
            },

            {
                field: "addedby",
                headerName: "Updated By",
                width: 130,
            },

        ],

        [activeTab]

    );


    // ========================================================
    // AUXILIARY TOTALS
    // ========================================================

    const auxiliaryTotals = useMemo(() => {

        const source =
            activeTab === "refund"
                ? refundRows
                : dueRows;


        return source.reduce(

            (totals, row) => ({

                invoiced:
                    totals.invoiced +
                    toNumber(row.invoice_amount),

                paid:
                    totals.paid +
                    toNumber(row.paid_amount),

                refund:
                    totals.refund +
                    toNumber(row.refund_amount),

                due:
                    totals.due +
                    toNumber(row.due_amount),

                balance:
                    totals.balance +
                    toNumber(row.balance),

            }),

            {
                invoiced: 0,
                paid: 0,
                refund: 0,
                due: 0,
                balance: 0,
            }

        );

    }, [activeTab, refundRows, dueRows]);


    // ========================================================
    // FILTER RESET
    // ========================================================

    const clearFilters = () => {

        setStartDate(null);

        setEndDate(null);

        setCashier("");

        setConsultant("all");

        setMode("");

    };


    const isAuxiliaryTab = [
        "refund",
        "paidDues",
    ].includes(activeTab);


    const activeAuxiliaryRows =
        activeTab === "refund"
            ? refundRows
            : dueRows;


    // ========================================================
    // UI
    // ========================================================

    return (

        <Box className="payment-ip-page">


            {/* HEADER */}

            <Box className="payment-ip-header">

                <Box>

                    <Typography
                        variant="h5"
                        className="payment-ip-title"
                    >
                        OP Payment Detail Report
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Outpatient invoicing, collections, refunds,
                        discounts and dues
                    </Typography>

                </Box>


                <Button
                    variant="contained"
                    onClick={fetchData}
                    disabled={isLoading}
                >
                    Refresh
                </Button>

            </Box>


            {/* FILTERS */}

            <Card className="payment-ip-filter-card">

                <CardContent>

                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                    >


                        {/* FROM DATE */}

                        <Grid item xs={12} sm={6} md={2}>

                            <Typography
                                variant="body2"
                                sx={{ mb: 0.6 }}
                            >
                                From Date
                            </Typography>

                            <DatePicker
                                selected={startDate}
                                onChange={setStartDate}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                maxDate={endDate || undefined}
                                placeholderText="From Date"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                isClearable
                                className="date-picker-input"
                                wrapperClassName="payment-ip-date-wrapper"
                            />

                        </Grid>


                        {/* TO DATE */}

                        <Grid item xs={12} sm={6} md={2}>

                            <Typography
                                variant="body2"
                                sx={{ mb: 0.6 }}
                            >
                                To Date
                            </Typography>

                            <DatePicker
                                selected={endDate}
                                onChange={setEndDate}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate || undefined}
                                placeholderText="To Date"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                isClearable
                                className="date-picker-input"
                                wrapperClassName="payment-ip-date-wrapper"
                            />

                        </Grid>


                        {/* CASHIER */}

                        <Grid item xs={12} sm={6} md={2}>

                            <FormControl fullWidth size="small">

                                <InputLabel id="op-cashier-label">
                                    Search by Cashier
                                </InputLabel>

                                <Select
                                    labelId="op-cashier-label"
                                    label="Search by Cashier"
                                    value={cashier}
                                    onChange={(event) =>
                                        setCashier(event.target.value)
                                    }
                                >

                                    <MenuItem value="">
                                        All Cashiers
                                    </MenuItem>

                                    {userData.map((user) => (

                                        <MenuItem
                                            key={user.id ?? user.username}
                                            value={user.username}
                                        >
                                            {user.username}
                                        </MenuItem>

                                    ))}

                                </Select>

                            </FormControl>

                        </Grid>


                        {/* CONSULTANT */}

                        <Grid item xs={12} sm={6} md={2}>

                            <FormControl fullWidth size="small">

                                <InputLabel id="op-consultant-label">
                                    Consultant
                                </InputLabel>

                                <Select
                                    labelId="op-consultant-label"
                                    label="Consultant"
                                    value={consultant}
                                    onChange={(event) =>
                                        setConsultant(event.target.value)
                                    }
                                >

                                    <MenuItem value="all">
                                        All Consultants
                                    </MenuItem>

                                    {consultants.map((name) => (

                                        <MenuItem
                                            key={name}
                                            value={name}
                                        >
                                            {name}
                                        </MenuItem>

                                    ))}

                                </Select>

                            </FormControl>

                        </Grid>


                        {/* PAYMENT MODE */}

                        <Grid item xs={12} sm={6} md={2}>

                            <FormControl fullWidth size="small">

                                <InputLabel id="op-mode-label">
                                    Payment Mode
                                </InputLabel>

                                <Select
                                    labelId="op-mode-label"
                                    label="Payment Mode"
                                    value={mode}
                                    onChange={(event) =>
                                        setMode(event.target.value)
                                    }
                                >

                                    <MenuItem value="">
                                        All Modes
                                    </MenuItem>

                                    <MenuItem value="cash">
                                        Cash
                                    </MenuItem>

                                    <MenuItem value="card">
                                        Card
                                    </MenuItem>

                                    <MenuItem value="creditpay">
                                        Credit Payment
                                    </MenuItem>

                                    <MenuItem value="UPI">
                                        UPI
                                    </MenuItem>

                                </Select>

                            </FormControl>

                        </Grid>


                        {/* CLEAR FILTERS */}

                        <Grid item xs={12} sm={6} md={2}>

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={clearFilters}
                            >
                                Clear Filters
                            </Button>

                        </Grid>


                    </Grid>

                </CardContent>

            </Card>


            {/* REPORT CARD */}

            <Card className="payment-ip-report-card">


                {/* REPORT TABS */}

                <Box className="payment-ip-tabs">

                    <Tabs
                        value={activeTab}
                        onChange={(_event, value) =>
                            setActiveTab(value)
                        }
                        variant="scrollable"
                        scrollButtons="auto"
                    >

                        <Tab
                            label="Invoiced"
                            value="invoiced"
                        />

                        <Tab
                            label="Collected"
                            value="collected"
                        />

                        <Tab
                            label="Refund"
                            value="refund"
                        />

                        <Tab
                            label="Paid Dues"
                            value="paidDues"
                        />

                        <Tab
                            label="Discount"
                            value="discount"
                        />

                        <Tab
                            label="Pending Dues"
                            value="pending"
                        />

                    </Tabs>

                </Box>


                <CardContent>


                    {/* SUMMARY */}

                    <Box className="payment-ip-summary">

                        <Box>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {reportTitle}
                            </Typography>

                            <Typography
                                variant="h5"
                                className="payment-ip-grand-total"
                            >

                                {isAuxiliaryTab

                                    ? `₹${money(
                                        activeTab === "refund"
                                            ? auxiliaryTotals.refund
                                            : auxiliaryTotals.due
                                    )}`

                                    : `₹${money(
                                        categoryTotals.grandTotal
                                    )}`

                                }

                            </Typography>

                        </Box>


                        <Chip
                            label={
                                isAuxiliaryTab
                                    ? `${activeAuxiliaryRows.length} records`
                                    : `${reportRows.length} records`
                            }
                            color="primary"
                            variant="outlined"
                        />


                        {consultant !== "all" && (

                            <Chip
                                label={`Consultant: ${consultant}`}
                                color="success"
                                variant="outlined"
                            />

                        )}

                    </Box>


                    {/* =================================================
                        CATEGORY CARDS
                    ================================================= */}

                    {!isAuxiliaryTab && (

                        <Grid
                            container
                            spacing={1.5}
                            className="payment-ip-category-cards"
                        >

                            {CATEGORIES.map((category) => (

                                <Grid
                                    item
                                    xs={6}
                                    sm={4}
                                    md={3}
                                    lg={2}
                                    key={category}
                                >

                                    <Box className="payment-ip-category-card">

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {category}
                                        </Typography>


                                        <Typography variant="h6">
                                            ₹{money(
                                                categoryTotals[category]
                                            )}
                                        </Typography>


                                        {activeTab !== "discount" && (

                                            <Typography
                                                variant="caption"
                                                color="error.main"
                                                sx={{
                                                    display: "block",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Discount: ₹
                                                {money(
                                                    categoryDiscountTotals[category]
                                                )}
                                            </Typography>

                                        )}

                                    </Box>

                                </Grid>

                            ))}

                        </Grid>

                    )}


                    {/* ERROR */}

                    {error && (

                        <Box className="payment-ip-error">
                            {error}
                        </Box>

                    )}


                    {/* LOADING */}

                    {isLoading ? (

                        <Box className="payment-ip-loading">

                            <CircularProgress size={32} />

                            <Typography variant="body2">
                                Loading report...
                            </Typography>

                        </Box>

                    ) : isAuxiliaryTab ? (


                        // =================================================
                        // REFUND / DUE TABLE
                        // =================================================

                        <Box className="payment-ip-grid">

                            <DataGrid
                                rows={activeAuxiliaryRows}
                                columns={auxiliaryColumns}
                                getRowId={(row) => row.id}
                                autoHeight
                                slots={{
                                    toolbar: GridToolbar,
                                }}
                                pageSizeOptions={[
                                    10,
                                    25,
                                    50,
                                    100,
                                ]}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            page: 0,
                                            pageSize: 10,
                                        },
                                    },
                                }}
                                disableRowSelectionOnClick
                                density="compact"
                            />


                            <Box className="payment-ip-summary">

                                <Typography variant="body2">
                                    Total Invoiced: ₹
                                    {money(auxiliaryTotals.invoiced)}
                                </Typography>

                                <Typography variant="body2">
                                    Total Paid: ₹
                                    {money(auxiliaryTotals.paid)}
                                </Typography>


                                {activeTab === "refund" ? (

                                    <Typography variant="body2">
                                        Total Refund: ₹
                                        {money(auxiliaryTotals.refund)}
                                    </Typography>

                                ) : (

                                    <Typography variant="body2">
                                        Total Due: ₹
                                        {money(auxiliaryTotals.due)}
                                    </Typography>

                                )}


                                <Typography variant="body2">
                                    Total Balance: ₹
                                    {money(auxiliaryTotals.balance)}
                                </Typography>

                            </Box>

                        </Box>


                    ) : (


                        // =================================================
                        // INVOICED / COLLECTION / DISCOUNT TABLE
                        // =================================================

                        <Box className="payment-ip-grid">

                            <DataGrid

                                rows={reportRows}

                                columns={paymentColumns}

                                getRowId={(row) => row.id}

                                autoHeight

                                slots={{
                                    toolbar: GridToolbar,
                                }}

                                pageSizeOptions={[
                                    10,
                                    25,
                                    50,
                                    100,
                                ]}

                                initialState={{

                                    pagination: {

                                        paginationModel: {
                                            page: 0,
                                            pageSize: 10,
                                        },

                                    },

                                    // Audiology and Dental hidden
                                    // by default.
                                    //
                                    // Users can enable them from
                                    // Toolbar > Columns.

                                    columns: {

                                        columnVisibilityModel: {

                                            category_Audiology: false,

                                            category_Dental: false,

                                        },

                                    },

                                }}

                                disableRowSelectionOnClick

                                density="compact"

                            />


                            {/* CATEGORY TOTALS */}

                            <Box className="payment-ip-summary">

                                {CATEGORIES.map((category) => (

                                    <Typography
                                        variant="body2"
                                        key={category}
                                    >

                                        {category}: ₹
                                        {money(
                                            categoryTotals[category]
                                        )}

                                    </Typography>

                                ))}


                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 800 }}
                                >

                                    Total: ₹
                                    {money(categoryTotals.grandTotal)}

                                </Typography>


                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 800,
                                        color: "error.main",
                                    }}
                                >

                                    Total Discount: ₹
                                    {money(
                                        CATEGORIES.reduce(
                                            (sum, category) =>
                                                sum +
                                                categoryDiscountTotals[category],
                                            0
                                        )
                                    )}

                                </Typography>

                            </Box>

                        </Box>

                    )}


                </CardContent>

            </Card>

        </Box>

    );

}


export default PaymentDetail;
